import * as Sentry from '@sentry/browser'
import * as React from 'react'
import styled from 'styled-components'
import { getWindow } from '../global'
import { IntroText, Spacer, BodyText } from './core'

interface IErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

interface IErrorBoundaryProps {
  componentName: string
}

/**
 * This component prevents exceptions thrown by its children from interfering with the rendering
 * of the rest of the tree. In the case of an error, this component renders an explanation of
 * which specific component threw the exception, as well as the stack trace.
 *
 * It is tempting to stick this into your render method as the parent of all the children of your
 * component. This is not the way! Wrap individual components with `InlineErrorBoundary` in order
 * to be explicit about its usage, and for it to work in the case of graphql wrapped components.
 */
export class InlineErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
  private errorContainerRef: React.RefObject<HTMLDivElement>

  constructor(props: InlineErrorBoundary['props']) {
    super(props)

    this.state = { hasError: false, error: null }
    this.errorContainerRef = React.createRef()
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: Record<string, any>) {
    console.log('caught error', error, info)
    Sentry.captureException(error)
  }

  onClick = () => {
    const selection = getWindow()!.getSelection()
    if (selection && this.errorContainerRef.current) {
      selection.selectAllChildren(this.errorContainerRef.current)
    }
  }

  render() {
    if (this.state.hasError) {
      return this.renderError()
    }
    return this.props.children
  }

  renderError() {
    return (
      <ErrorBoundaryContainer onClick={this.onClick} ref={this.errorContainerRef}>
        <IntroText color="coral">
          <strong>{`Error Rendering Component '${this.props.componentName}'`}</strong>
        </IntroText>
        <Spacer height={16} />
        <BodyText color="white">
          <pre>{this.state.error && this.state.error.stack}</pre>
          <br />
          <pre>{navigator.userAgent}</pre>
          <pre>{getWindow()!.location.href}</pre>
        </BodyText>
      </ErrorBoundaryContainer>
    )
  }
}

const ErrorBoundaryContainer = styled.div`
  cursor: pointer;
  margin: 8px;
  padding: 16px;
  background-color: rgb(100, 50, 50);
  border: 1px solid rgb(100%, 26.5%, 26.5%);
  min-width: 600px;
  max-width: 800px;
  overflow-x: scroll;
  z-index: 100;
`
